import { backEndUserAdapter, frontEndUserAdapter } from '@egovsolutions/angular-egov-api/session/common';
export * from '@egovsolutions/angular-egov-api/session/common';
import * as i0 from '@angular/core';
import { Injectable } from '@angular/core';
import { Subject, throwError, map, tap } from 'rxjs';
import * as i2 from '@egovsolutions/angular-egov-api/session/internal';
import { EgovSessionAbstract } from '@egovsolutions/angular-egov-api/session/internal';
import * as i1 from '@egovsolutions/angular-egov-api/common';

class RealEgovSession extends EgovSessionAbstract {
    constructor(api, responses, lifetimeManager) {
        super(api, responses, lifetimeManager);
        // Keep alive
        this.callingKeepAlive = false;
    }
    // Sign up
    signUp$(values) {
        /* eslint-disable @typescript-eslint/naming-convention */
        return this.api.request$({
            endpoint: `${this.endpointBase}/signup`,
            bodyParameters: {
                first_name: values.first,
                last_name: values.last,
                email: values.email,
                password: values.password,
                phone: values.phone,
            }
        });
        /* eslint-enable @typescript-eslint/naming-convention */
    }
    validateEmail$(email, verificationCode) {
        /* eslint-disable @typescript-eslint/naming-convention */
        return this.api.request$({
            endpoint: `${this.endpointBase}/checkvalidationcode`,
            bodyParameters: {
                email,
                validation_code: verificationCode
            }
        });
        /* eslint-enable @typescript-eslint/naming-convention */
    }
    // Sign in
    getPasswordResetCode$(email) {
        return this.api.request$({
            endpoint: `${this.endpointBase}/sendvalidationcode`,
            bodyParameters: { email },
        });
    }
    resetPassword$(email, verificationCode, newPassword) {
        /* eslint-disable @typescript-eslint/naming-convention */
        return this.api.request$({
            endpoint: `${this.endpointBase}/resetpw`,
            bodyParameters: {
                email,
                validation_code: verificationCode,
                password: newPassword,
            }
        });
        /* eslint-enable @typescript-eslint/naming-convention */
    }
    keepAlive() {
        if (!this.callingKeepAlive) {
            this.callingKeepAlive = true;
            const subject = new Subject();
            const handleResponse = (response) => {
                this.callingKeepAlive = false;
                if (response?.session) {
                    if (response.session === 'alive') {
                        subject.next();
                        subject.complete();
                    }
                    else {
                        subject.error('Session died');
                    }
                }
                else
                    subject.error('Session value missing');
            };
            this.api.request$({ endpoint: `${this.endpointBase}/keepalive`, method: 'POST' }).subscribe({
                next: handleResponse,
                error: error => handleResponse(error.response),
            });
            return subject.asObservable();
        }
        else
            return throwError(() => 'Already sending a keep alive.');
    }
    // Update details
    updateAccountDetails$(details) {
        return this.api.request$({
            endpoint: `${this.endpointBase}/update`,
            bodyParameters: details,
        })
            .pipe(map(this.adaptSession));
    }
    updatePassword$(currentPassword, newPassword) {
        return this.api.request$({
            endpoint: `${this.endpointBase}/update`,
            bodyParameters: {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                current_password: currentPassword,
                password: newPassword,
            }
        })
            .pipe(map(this.adaptSession));
    }
    // Sign out
    signOut$() {
        return this.api.request$({ endpoint: `${this.endpointBase}/logout`, method: 'POST' }).pipe(tap(() => this.flushSession()));
    }
}

class BackEndSession extends RealEgovSession {
    constructor(api, lifetimeManager, responses) {
        super(api, responses, lifetimeManager);
        this.endpointBase = 'auth';
        this.adaptSession = backEndUserAdapter;
    }
    signIn$(username, password, location) {
        return this.api.request$({
            endpoint: 'auth/suite/login',
            bodyParameters: {
                username,
                password,
                location,
            }
        }).pipe(map(this.adaptSession));
    }
}
BackEndSession.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: BackEndSession, deps: [{ token: i1.EgovAPI }, { token: i2.SessionLifetimeManager }, { token: i1.EgovAPIResponses }], target: i0.ɵɵFactoryTarget.Injectable });
BackEndSession.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: BackEndSession, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: BackEndSession, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.EgovAPI }, { type: i2.SessionLifetimeManager }, { type: i1.EgovAPIResponses }]; } });

class FrontEndSession extends RealEgovSession {
    constructor(api, lifetimeManager, responses) {
        super(api, responses, lifetimeManager);
        this.endpointBase = 'auth';
        this.adaptSession = frontEndUserAdapter;
    }
    // Sign in
    signIn$(email, password) {
        return this.api.request$({
            endpoint: `${this.endpointBase}/login`,
            bodyParameters: { email, password },
        }).pipe(map(this.adaptSession));
    }
}
FrontEndSession.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: FrontEndSession, deps: [{ token: i1.EgovAPI }, { token: i2.SessionLifetimeManager }, { token: i1.EgovAPIResponses }], target: i0.ɵɵFactoryTarget.Injectable });
FrontEndSession.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: FrontEndSession, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: FrontEndSession, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.EgovAPI }, { type: i2.SessionLifetimeManager }, { type: i1.EgovAPIResponses }]; } });

/**
 * Generated bundle index. Do not edit.
 */

export { BackEndSession, FrontEndSession };
